import { apiService } from './api.service.ts';
import { API_ENDPOINTS } from '../config/api.config.ts';
import { deviceService } from './device.service.ts';
import { logger } from './logger.service.ts';
import { storageService, StorageKey } from './storage.service.ts';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface UserProfile {
  profile_id: string;
  user_id: number;
  language: string;
  timezone: string | null;
  notification_preferences: {
    sms: boolean;
    push: boolean;
    email: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface UserDevice {
  device_id: number;
  user_id: number;
  device_token: string;
  device_type: string;
  hardware_id: string;
  last_login: string;
  is_active: boolean;
  created_at: string;
}

export interface User {
  user_id: number;
  phone_number: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_image: string | null;
  user_type: string;
  role: string;
  status: string;
  email_verified_at: string | null;
  phone_verified_at: string | null;
  last_login_at: string;
  last_password_change: string | null;
  metadata: any;
  max_concurrent_orders: number;
  created_at: string;
  updated_at: string;
  profile: UserProfile;
  devices: UserDevice[];
}

export interface AuthResponse {
  user: User;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: string;
}

class AuthService {

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Get device headers from device service
    const headers = deviceService.getDeviceHeaders();

    // Log login request headers
    logger.debug('Login request headers:', headers);

    try {
      const response = await apiService.post(
        API_ENDPOINTS.auth.login,
        credentials,
        { headers }
      );

      const data = response.data;

      if (data.access_token) {
        storageService.set(StorageKey.TOKEN, data.access_token);
        storageService.set(StorageKey.REFRESH_TOKEN, data.refresh_token);
        storageService.set(StorageKey.USER, data.user);
      }

      return data;
    } catch (error) {
      logger.error('Login request error:', error);
      throw error;
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    const refresh_token = storageService.get<string>(StorageKey.REFRESH_TOKEN);
    if (!refresh_token) {
      throw new Error('No refresh token found');
    }

    const response = await apiService.post(API_ENDPOINTS.auth.refresh, {
      refresh_token
    });
    const data = response.data;

    if (data.access_token) {
      storageService.set(StorageKey.TOKEN, data.access_token);
      storageService.set(StorageKey.REFRESH_TOKEN, data.refresh_token);
    }

    return data;
  }

  async logout(): Promise<void> {
    await apiService.post(API_ENDPOINTS.auth.logout);
    storageService.remove(StorageKey.TOKEN);
    storageService.remove(StorageKey.REFRESH_TOKEN);
    storageService.remove(StorageKey.USER);
    deviceService.clearDeviceId(); // Use device service to clear device ID
  }

  isAuthenticated(): boolean {
    return storageService.has(StorageKey.TOKEN);
  }

  getUser(): User | null {
    return storageService.get<User>(StorageKey.USER);
  }

  // Add a debug method for direct header testing (only in development)
  async testDeviceHeaders(): Promise<void> {
    if (!import.meta.env.DEV) return;

    const deviceId = deviceService.getDeviceId();
    logger.debug('Test device ID:', deviceId);

    // Use direct axios call to test headers
    const axios = await import('axios');
    try {
      // Make a simple OPTIONS request to check headers
      const response = await axios.default.options(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}${API_ENDPOINTS.auth.login}`,
        {
          headers: deviceService.getDeviceHeaders()
        }
      );
      logger.debug('Headers test response:', response);
    } catch (error) {
      logger.warn('Headers test error (this may be normal):', error);
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Also export as default for compatibility with JavaScript imports
export default authService;